import { useMemo, useEffect, useCallback } from 'react'
import { eachDayOfInterval, format, addMonths, subMonths, isSameMonth } from 'date-fns'
import { useQuery, useMutation } from 'convex/react'
import { api } from '../../convex/_generated/api'
import type { Id } from '../../convex/_generated/dataModel'
import { useCalendarStore, type CalendarEntry, type CalendarChild } from '../stores/calendarStore'
import { useAuthStore } from '../stores/authStore'

type Location = 'home_a' | 'home_b' | null

/**
 * Map Convex calendar entry to compatibility shape (snake_case)
 * so existing components (DayCell, CombinedModal) work unchanged.
 */
function mapConvexEntry(entry: any): CalendarEntry {
  return {
    id: entry._id,
    child_id: entry.childId,
    family_id: entry.familyId,
    date: entry.date,
    location: entry.location,
    comment: entry.comment ?? null,
    is_encrypted: entry.isEncrypted ?? false,
    created_by: entry.createdBy ?? '',
    created_at: new Date(entry._creationTime).toISOString(),
    updated_at: new Date(entry._creationTime).toISOString(),
  }
}

/**
 * Map Convex child to compatibility shape (snake_case).
 */
function mapConvexChild(child: any): CalendarChild {
  return {
    id: child._id,
    family_id: child.familyId,
    name: child.name,
    birth_date: child.birthDate ?? null,
    created_at: new Date(child._creationTime).toISOString(),
  }
}

export function useCalendar() {
  // ── UI state from Zustand store ──
  const selectedChildId = useCalendarStore(state => state.selectedChildId)
  const selectedHome = useCalendarStore(state => state.selectedHome)
  const setSelectedChild = useCalendarStore(state => state.setSelectedChild)
  const setSelectedHome = useCalendarStore(state => state.setSelectedHome)
  const setStoreCurrentDate = useCalendarStore(state => state.setCurrentDate)
  const setStoreViewMode = useCalendarStore(state => state.setViewMode)
  const storeCurrentDate = useCalendarStore(state => state.currentDate)
  const storeViewMode = useCalendarStore(state => state.viewMode)
  const storeEntries = useCalendarStore(state => state.entries)
  const storeChildren = useCalendarStore(state => state.children)
  const setEntries = useCalendarStore(state => state.setEntries)
  const setChildren = useCalendarStore(state => state.setChildren)
  const setIsLoading = useCalendarStore(state => state.setIsLoading)
  const setError = useCalendarStore(state => state.setError)
  const isLoading = useCalendarStore(state => state.isLoading)
  const error = useCalendarStore(state => state.error)

  const { user } = useAuthStore()

  const currentDate = useMemo(() => new Date(storeCurrentDate), [storeCurrentDate])
  const viewMode = storeViewMode

  // ── Calendar range calculation ──
  const fullCalendarRange = useMemo(() => {
    const year = currentDate.getFullYear()
    const month = currentDate.getMonth()
    const firstDayOfMonth = new Date(year, month, 1)
    const lastDayOfMonth = new Date(year, month + 1, 0)
    const firstDayWeekday = firstDayOfMonth.getDay()
    const adjustedFirstDayWeekday = firstDayWeekday === 0 ? 6 : firstDayWeekday - 1
    const firstDisplayDay = new Date(firstDayOfMonth)
    firstDisplayDay.setDate(firstDayOfMonth.getDate() - adjustedFirstDayWeekday)
    const lastDisplayDay = new Date(lastDayOfMonth)
    const lastDayWeekday = lastDayOfMonth.getDay()
    const adjustedLastDayWeekday = lastDayWeekday === 0 ? 6 : lastDayWeekday - 1
    const remainingDays = 6 - adjustedLastDayWeekday
    lastDisplayDay.setDate(lastDayOfMonth.getDate() + remainingDays)
    return { start: firstDisplayDay, end: lastDisplayDay }
  }, [currentDate])

  const startDate = useMemo(() => format(fullCalendarRange.start, 'yyyy-MM-dd'), [fullCalendarRange])
  const endDate = useMemo(() => format(fullCalendarRange.end, 'yyyy-MM-dd'), [fullCalendarRange])

  // ── Get user's family ID from Convex (self-contained, no familyStore dependency) ──
  const familyId = useQuery(api.families.queries.getUserFamilyIdQuery)
  // familyId: undefined (loading) | null (no family) | Id<"families">

  // ── Convex reactive queries ──
  const isValidChildId = !!(selectedChildId && selectedChildId !== 'temp-child-id')

  const convexEntries = useQuery(
    api.calendar.queries.getEntries,
    isValidChildId
      ? { childId: selectedChildId as Id<"children">, startDate, endDate }
      : "skip"
  )

  const convexChildren = useQuery(
    api.families.queries.getChildren,
    familyId ? { familyId: familyId as Id<"families"> } : "skip"
  )

  // ── Convex mutations ──
  const addEntryMutation = useMutation(api.calendar.mutations.addEntry)
  const updateEntryMutation = useMutation(api.calendar.mutations.updateEntry)
  const deleteEntryMutation = useMutation(api.calendar.mutations.deleteEntry)

  // ── Sync Convex data → Zustand store (for DayCell / CombinedModal compatibility) ──
  useEffect(() => {
    if (convexEntries !== undefined) {
      setEntries(convexEntries.map(mapConvexEntry))
      setIsLoading(false)
    } else if (isValidChildId) {
      setIsLoading(true)
    } else {
      // No valid child — empty entries, not loading
      setEntries([])
      setIsLoading(false)
    }
  }, [convexEntries, isValidChildId, setEntries, setIsLoading])

  useEffect(() => {
    if (convexChildren !== undefined) {
      setChildren(convexChildren.map(mapConvexChild))
    }
  }, [convexChildren, setChildren])

  // ── Temporary child fallback ──
  const displayChildren = useMemo(() => {
    if (storeChildren.length === 0 && user && convexChildren !== undefined) {
      const tempChild: CalendarChild = {
        id: 'temp-child-id',
        family_id: familyId ?? 'temporary-family-id',
        name: 'Enter children',
        birth_date: null,
        created_at: new Date().toISOString()
      }
      if (!selectedChildId) {
        setSelectedChild('temp-child-id')
      }
      return [tempChild]
    }
    return storeChildren
  }, [storeChildren, user, convexChildren, familyId, selectedChildId, setSelectedChild])

  // ── Derived calendar data ──
  const calendarDays = useMemo(() => {
    const allDays = eachDayOfInterval({
      start: fullCalendarRange.start,
      end: fullCalendarRange.end
    })
    return allDays.map(day => {
      const dateString = format(day, 'yyyy-MM-dd')
      const dayEntries = storeEntries.filter(entry => entry.date === dateString)
      const dayEntry = dayEntries[0]

      return {
        date: day,
        dateString,
        isCurrentMonth: isSameMonth(day, currentDate),
        location: dayEntry?.location || null,
        comment: dayEntry?.comment || null
      }
    })
  }, [fullCalendarRange, storeEntries, currentDate])

  const monthName = useMemo(() => {
    return format(currentDate, 'MMMM yyyy')
  }, [currentDate])

  // ── Navigation ──
  const nextMonth = useCallback(() => {
    setStoreCurrentDate(format(addMonths(currentDate, 1), 'yyyy-MM-dd'))
  }, [currentDate, setStoreCurrentDate])

  const prevMonth = useCallback(() => {
    setStoreCurrentDate(format(subMonths(currentDate, 1), 'yyyy-MM-dd'))
  }, [currentDate, setStoreCurrentDate])

  const nextWeek = useCallback(() => {
    const newDate = new Date(currentDate)
    newDate.setDate(currentDate.getDate() + 7)
    setStoreCurrentDate(format(newDate, 'yyyy-MM-dd'))
  }, [currentDate, setStoreCurrentDate])

  const prevWeek = useCallback(() => {
    const newDate = new Date(currentDate)
    newDate.setDate(currentDate.getDate() - 7)
    setStoreCurrentDate(format(newDate, 'yyyy-MM-dd'))
  }, [currentDate, setStoreCurrentDate])

  // ── Calendar entry mutation (add/update/delete) ──
  const updateCalendarEntry = useCallback(async (
    date: string,
    _segment: 'day',
    location: Location,
    comment?: string
  ) => {
    const childId = selectedChildId || 'temp-child-id'
    if (!childId || childId === 'temp-child-id' || !user || !familyId) return

    try {
      setError(null)
      const existingEntry = storeEntries.find(entry => entry.date === date)

      // Delete if location is null
      if (location === null && existingEntry) {
        await deleteEntryMutation({
          entryId: existingEntry.id as Id<"calendarEntries">
        })
        return
      }

      // Update existing entry
      if (existingEntry) {
        await updateEntryMutation({
          entryId: existingEntry.id as Id<"calendarEntries">,
          location: location as 'home_a' | 'home_b',
          comment: comment || existingEntry.comment || undefined,
        })
        return
      }

      // Add new entry
      if (location) {
        await addEntryMutation({
          childId: childId as Id<"children">,
          familyId: familyId as Id<"families">,
          date,
          location: location as 'home_a' | 'home_b',
          comment: comment || undefined,
        })
      }
    } catch (err: any) {
      setError(err.message || 'Error updating calendar entry')
    }
  }, [selectedChildId, user, familyId, storeEntries, deleteEntryMutation, updateEntryMutation, addEntryMutation, setError])

  return {
    currentDate,
    monthName,
    calendarDays,
    viewMode,
    entries: storeEntries,
    children: displayChildren,
    selectedChildId: selectedChildId || 'temp-child-id',
    selectedHome,
    isLoading,
    error,
    nextMonth,
    prevMonth,
    nextWeek,
    prevWeek,
    setViewMode: setStoreViewMode,
    setSelectedChild,
    setSelectedHome,
    updateCalendarEntry
  }
}
